import {
  Box,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  useTheme,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import PublishIcon from '@mui/icons-material/Publish'
import DeleteIcon from '@mui/icons-material/Delete'

import { GridItemCollapse } from './item-collapse.component'
import { EScanAccountUserRoles, EScanStatus, EUserRoles, IScanRespDto } from 'src/types'

import { useStyles } from './styles'
// import { ScanStatus } from './status.component'
import {
  useCancelScanRemove,
  useDeleteScan,
  useItemsRemoveConfirm,
  useNotification,
  usePushScanToBc,
 
  useUpdateScanStatus,
} from 'src/hooks'
import { ChevronUpIcon } from 'src/assets/icons/v2/ChevronUpIcon'
import { ChevronDownIcon } from 'src/assets/icons/v2/ChevronDownIcon'
import {  useEffect, useState } from 'react'
import { TextTag } from 'src/components/text-tag'
import { getColorOrNameByScanStatus } from 'src/common/utils/get-color-or name-by-scan-status'
import { ScanRecordIcon } from '../scan-record-icon'
import { TrashTimeoutIcon } from 'src/assets/icons'
import { useTranslation } from 'react-i18next'

interface IChangesGridItemProps {
  handleClick?: () => void
  item: IScanRespDto
  isScaner: boolean
  isOpen: boolean
  userRole: EScanAccountUserRoles
  currentUserRoleAndId: {
    id: string
    roles: string[]
  } | null
}

export const GridItem = ({
  item,
  handleClick,
  isScaner,
  isOpen = false,
  userRole,
  currentUserRoleAndId,
}: IChangesGridItemProps) => {
  const [itemStatus, setItemStatus] = useState<EScanStatus>(item.status)
  const [isPushDisabled, setPushIsDisabled] = useState<boolean>(false)
  const [toggle, setToggle] = useState(isOpen)
  const classes = useStyles()

  const { mutate: updateScanStatus, isLoading: isUpdatingScanStatus } = useUpdateScanStatus()
  const { mutate: pushScanToBc, isLoading: isPushing } = usePushScanToBc()

  const { mutateAsync: deleteScan, isLoading: isDeleting } = useDeleteScan()
  const { notify } = useNotification()
  const { mutateAsync: cancelScanRemove } = useCancelScanRemove()
  const { setRemoveConfirm } = useItemsRemoveConfirm()

  const [t] = useTranslation()
  const theme = useTheme()

  useEffect(() => {
    setToggle(isOpen)
  }, [isOpen])

  useEffect(() => {
    setItemStatus(item.status)
  }, [item.status])

  const handleCollapse = () => {
    setToggle((toggle) => !toggle)
  }

  const handleDeleteItem = async () => {
    if (!item || !item.id) return

    setRemoveConfirm({
      handleConfirm: async () => {
        const resp = await deleteScan(item.id)

        if (!resp) return

        notify({
          message: t('items-delete-notification.v'),
          icon: <TrashTimeoutIcon />,
          onButtonClick: () => cancelScanRemove(item.id),
          timeout: 14000,
        })
      },
      itemsTitles: [],
      itemsRelations: [],
      itemsDetails: item
        ? [
            { title: 'Scanner', text: item?.scanner_name || item.scanner_email },
            { title: 'Status', text: getColorOrNameByScanStatus('text', itemStatus) },
          ]
        : [],
      itemsType: 'scan',
    })
  }

  const handlePushToBc = () => {
    setPushIsDisabled(true)
    setItemStatus(EScanStatus.ON_CHAIN)
    pushScanToBc(item.id)
  }

  const handleScanStatus = (status: EScanStatus) => {
    setItemStatus(status)
    updateScanStatus({ id: item.id, status, accountId: item.account?.id })
  }

  return (
    <>
      <ListItemButton onClick={handleCollapse} disableGutters className={classes.listItemButton}>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <ListItemIcon>
            {toggle ? (
              <ChevronUpIcon size={28} color={theme.palette.custom.darkBlue2} />
            ) : (
              <ChevronDownIcon size={28} color={theme.palette.custom.darkBlue2} />
            )}
          </ListItemIcon>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              gap: '10px',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <TextTag
                label={getColorOrNameByScanStatus('text', itemStatus)}
                bgColor={getColorOrNameByScanStatus('color', itemStatus)}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <ScanRecordIcon type={item.record_type} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '3px',
                flex: 1,
              }}
            >
              {(itemStatus === EScanStatus.NEW || itemStatus === EScanStatus.NOT_APPROVED) &&
                userRole !== EScanAccountUserRoles.USER &&
                userRole !== EScanAccountUserRoles.SCANNER && (
                  <Tooltip title="Approve Scan">
                    <IconButton
                      sx={{
                        padding: '4px',
                      }}
                      disabled={isUpdatingScanStatus}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleScanStatus(EScanStatus.APPROVED)
                      }}
                    >
                      <CheckIcon sx={{ color: 'green' }} />
                    </IconButton>
                  </Tooltip>
                )}
              {(itemStatus === EScanStatus.NEW || itemStatus === EScanStatus.APPROVED) &&
                userRole !== EScanAccountUserRoles.USER &&
                userRole !== EScanAccountUserRoles.SCANNER && (
                  <Tooltip title="Disapprove Scan">
                    <IconButton
                      sx={{
                        padding: '4px',
                      }}
                      disabled={isUpdatingScanStatus}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleScanStatus(EScanStatus.NOT_APPROVED)
                      }}
                    >
                      <ClearIcon sx={{ color: '#E57373' }} />
                    </IconButton>
                  </Tooltip>
                )}
              {itemStatus === EScanStatus.APPROVED &&
                userRole !== EScanAccountUserRoles.USER &&
                userRole !== EScanAccountUserRoles.SCANNER && (
                  <Tooltip title="Push On-Chain">
                    <IconButton
                      sx={{
                        padding: '4px',
                      }}
                      disabled={isPushing || isPushDisabled}
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePushToBc()
                      }}
                    >
                      <PublishIcon sx={{ color: '#6f3cce' }} />
                    </IconButton>
                  </Tooltip>
                )}

              {itemStatus !== EScanStatus.ON_CHAIN &&
                (userRole === EScanAccountUserRoles.ADMIN ||
                  userRole === EScanAccountUserRoles.OWNER ||
                  currentUserRoleAndId?.id === item.scanner?.id ||
                  currentUserRoleAndId?.roles?.includes(EUserRoles.superadmin)) && (
                  <Tooltip title="Delete">
                    <IconButton
                      sx={{
                        padding: '4px',
                      }}
                      disabled={isDeleting}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteItem()
                      }}
                    >
                      <DeleteIcon sx={{ color: theme.palette.custom.warning }} />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>
          </Box>

          {/* <ListItemText className={classes.collapseItem} disableTypography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Typography className={`${classes.itemText} ${classes.itemPropertyText}`}>
                  <HelperWrapper t="batch-id-table-column" ns={ETranslationNs.Scans} /> :
                </Typography>
                <Typography className={classes.itemText}>{item.batch_id || '-'}</Typography>
              </Box>
              <Box sx={{ minWidth: '32px' }}>
                <ScanStatus pushed={!!item.account_tx} approved={!!item.reviewed} />
              </Box>
            </Box>
          </ListItemText> */}
        </Box>
      </ListItemButton>
      <GridItemCollapse handleClick={handleClick} item={item} open={toggle} isScaner={isScaner} />
      <Divider />
    </>
  )
}
